<template>
  <main class="stats-page">
    <div class="container">

      <!-- Password gate -->
      <div v-if="!unlocked" class="gate">
        <h1 class="gate__title">Site Analytics</h1>
        <form class="gate__form" @submit.prevent="unlock">
          <label class="gate__label" for="stats-password">Password</label>
          <input
            id="stats-password"
            class="gate__input"
            type="password"
            v-model.trim="password"
            autocomplete="current-password"
          />
          <p v-if="error" class="gate__error">Incorrect password.</p>
          <button class="gate__btn" type="submit">View stats</button>
        </form>
      </div>

      <!-- Dashboard -->
      <div v-else>
        <div class="dash-header">
          <div>
            <h1 class="dash-title">Site Analytics</h1>
            <p class="dash-subtitle">greenslash.dev</p>
          </div>

          <!-- Date filter -->
          <div class="filter-wrap">
            <button class="filter-pill" @click.stop="toggleFilter">
              {{ activePillLabel }}
              <svg
                class="filter-pill__chevron"
                :class="{ 'filter-pill__chevron--open': filterOpen }"
                width="12" height="12" viewBox="0 0 12 12" fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <!-- Backdrop -->
            <div v-if="filterOpen" class="filter-backdrop" @click="closeFilter"></div>

            <!-- Panel -->
            <div v-if="filterOpen" class="filter-panel" @click.stop>
              <p class="filter-panel__heading">Date range</p>

              <div class="filter-presets">
                <label
                  v-for="p in presets"
                  :key="p.key"
                  class="filter-radio"
                  :class="{ 'filter-radio--active': panelPreset === p.key }"
                >
                  <input
                    type="radio"
                    :value="p.key"
                    v-model="panelPreset"
                  />
                  <span class="filter-radio__dot"></span>
                  <span class="filter-radio__label">{{ p.label }}</span>
                </label>
              </div>

              <!-- Custom date inputs -->
              <div v-if="panelPreset === 'custom'" class="filter-custom">
                <div class="filter-custom__field">
                  <label class="filter-custom__label">Start date</label>
                  <input
                    type="date"
                    class="filter-custom__input"
                    v-model="panelCustomFrom"
                  />
                </div>
                <div class="filter-custom__field">
                  <label class="filter-custom__label">End date</label>
                  <input
                    type="date"
                    class="filter-custom__input"
                    v-model="panelCustomTo"
                  />
                </div>
              </div>

              <button class="filter-apply" @click="applyFilter">Show results</button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="loading">Loading…</div>

        <div v-else>
          <!-- Summary stats -->
          <div class="stat-grid">
            <div class="stat-card">
              <p class="stat-card__label">Total page views</p>
              <p class="stat-card__value">{{ totalVisits.toLocaleString() }}</p>
            </div>
            <div class="stat-card">
              <p class="stat-card__label">Today</p>
              <p class="stat-card__value">{{ visitsToday.toLocaleString() }}</p>
            </div>
            <div class="stat-card">
              <p class="stat-card__label">This week</p>
              <p class="stat-card__value">{{ visitsThisWeek.toLocaleString() }}</p>
            </div>
            <div class="stat-card">
              <p class="stat-card__label">Total active time</p>
              <p class="stat-card__value">{{ formatDuration(totalActiveSeconds) }}</p>
            </div>
            <div class="stat-card">
              <p class="stat-card__label">Avg session time</p>
              <p class="stat-card__value">{{ formatDuration(avgSessionSeconds) }}</p>
            </div>
          </div>

          <!-- Project clicks -->
          <div class="section">
            <h2 class="section__title">Project card clicks</h2>
            <div v-if="projectClicks.length === 0" class="table-empty">No clicks recorded yet.</div>
            <table v-else class="data-table">
              <thead>
                <tr>
                  <th class="data-table__th">Project</th>
                  <th class="data-table__th data-table__th--right">Clicks</th>
                  <th class="data-table__th data-table__th--right">Last clicked</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in projectClicks" :key="row.name" class="data-table__row">
                  <td class="data-table__td">{{ row.name }}</td>
                  <td class="data-table__td data-table__td--right">
                    <span class="clicks-badge">{{ row.count }}</span>
                  </td>
                  <td class="data-table__td data-table__td--right data-table__td--muted">
                    {{ formatDate(row.lastClicked) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  </main>
</template>

<script>
import { supabase } from '../analytics/supabaseClient';

const STATS_PASSWORD = process.env.VUE_APP_STATS_PASSWORD;

const PRESETS = [
  { key: 'P7D',    label: 'Past 7 days',   days: 7 },
  { key: 'P14D',   label: 'Past 14 days',  days: 14 },
  { key: 'P28D',   label: 'Past 28 days',  days: 28 },
  { key: 'P90D',   label: 'Past 90 days',  days: 90 },
  { key: 'P365D',  label: 'Past 365 days', days: 365 },
  { key: 'custom', label: 'Custom',         days: null },
];

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfToday() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

export default {
  name: 'StatsPage',
  data() {
    return {
      // auth
      unlocked: sessionStorage.getItem('statsUnlocked') === 'true',
      password: '',
      error: false,
      loading: false,
      // filter UI
      filterOpen: false,
      activePreset: 'P7D',
      panelPreset: 'P7D',
      panelCustomFrom: '',
      panelCustomTo: '',
      // active date range (drives all queries)
      dateFilter: { from: daysAgo(7), to: endOfToday() },
      // stats
      totalVisits: 0,
      visitsToday: 0,
      visitsThisWeek: 0,
      totalActiveSeconds: 0,
      avgSessionSeconds: 0,
      projectClicks: [],
      // expose presets to template
      presets: PRESETS,
    };
  },
  computed: {
    activePillLabel() {
      if (this.activePreset === 'custom') return 'Custom range';
      return PRESETS.find(p => p.key === this.activePreset)?.label ?? 'Past 7 days';
    },
  },
  watch: {
    dateFilter: {
      deep: true,
      handler() {
        if (this.unlocked) this.fetchStats();
      },
    },
  },
  async mounted() {
    if (this.unlocked) {
      await this.fetchStats();
    }
  },
  methods: {
    async unlock() {
      if (STATS_PASSWORD && this.password === STATS_PASSWORD) {
        this.error = false;
        this.unlocked = true;
        sessionStorage.setItem('statsUnlocked', 'true');
        await this.fetchStats();
      } else {
        this.error = true;
      }
    },
    toggleFilter() {
      this.filterOpen = !this.filterOpen;
      if (this.filterOpen) {
        // Sync panel to current active state when opening
        this.panelPreset = this.activePreset;
      }
    },
    closeFilter() {
      this.filterOpen = false;
    },
    applyFilter() {
      if (this.panelPreset === 'custom') {
        if (!this.panelCustomFrom || !this.panelCustomTo) return;
        const from = new Date(this.panelCustomFrom);
        from.setHours(0, 0, 0, 0);
        const to = new Date(this.panelCustomTo);
        to.setHours(23, 59, 59, 999);
        this.dateFilter = { from, to };
      } else {
        const preset = PRESETS.find(p => p.key === this.panelPreset);
        this.dateFilter = { from: daysAgo(preset.days), to: endOfToday() };
      }
      this.activePreset = this.panelPreset;
      this.filterOpen = false;
    },
    async fetchStats() {
      this.loading = true;
      const from = this.dateFilter.from.toISOString();
      const to = this.dateFilter.to.toISOString();
      try {
        const [visitsRes, sessionsRes, clicksRes] = await Promise.all([
          supabase.from('visits')
            .select('created_at')
            .order('created_at', { ascending: false })
            .gte('created_at', from)
            .lte('created_at', to),
          supabase.from('sessions')
            .select('active_seconds')
            .gte('created_at', from)
            .lte('created_at', to),
          supabase.from('project_clicks')
            .select('project_name, created_at')
            .gte('created_at', from)
            .lte('created_at', to),
        ]);

        // Visits
        const visits = visitsRes.data ?? [];
        this.totalVisits = visits.length;

        const toDate = this.dateFilter.to;
        const weekBeforeTo = new Date(toDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        this.visitsToday = visits.filter(v => {
          const d = new Date(v.created_at);
          return d.getFullYear() === toDate.getFullYear() &&
            d.getMonth() === toDate.getMonth() &&
            d.getDate() === toDate.getDate();
        }).length;
        this.visitsThisWeek = visits.filter(v => new Date(v.created_at) >= weekBeforeTo).length;

        // Sessions
        const sessions = sessionsRes.data ?? [];
        this.totalActiveSeconds = sessions.reduce((sum, s) => sum + (s.active_seconds || 0), 0);
        this.avgSessionSeconds = sessions.length
          ? Math.round(this.totalActiveSeconds / sessions.length)
          : 0;

        // Clicks per project
        const clicks = clicksRes.data ?? [];
        const grouped = clicks.reduce((acc, c) => {
          if (!acc[c.project_name]) acc[c.project_name] = { count: 0, lastClicked: null };
          acc[c.project_name].count += 1;
          if (!acc[c.project_name].lastClicked || c.created_at > acc[c.project_name].lastClicked) {
            acc[c.project_name].lastClicked = c.created_at;
          }
          return acc;
        }, {});
        this.projectClicks = Object.entries(grouped)
          .sort(([, a], [, b]) => b.count - a.count)
          .map(([name, { count, lastClicked }]) => ({ name, count, lastClicked }));
      } finally {
        this.loading = false;
      }
    },
    formatDate(isoString) {
      if (!isoString) return '—';
      return new Date(isoString).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatDuration(totalSeconds) {
      if (!totalSeconds || totalSeconds <= 0) return '—';
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      if (h > 0) return `${h}h ${m}m`;
      if (m > 0) return `${m}m ${s}s`;
      return `${s}s`;
    },
  },
};
</script>

<style scoped>
.stats-page {
  min-height: 100vh;
  padding-block: var(--space-3xl);
  margin-top: 3.9rem;
}

.container {
  width: 100%;
  max-width: var(--grid-max-width);
  margin-inline: auto;
  padding-inline: var(--grid-padding);
}

/* ─── Gate ─────────────────────────────────────────────────────────────── */
.gate {
  max-width: 400px;
  margin: var(--space-3xl) auto 0;
}

.gate__title {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--space-8);
}

.gate__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.gate__label {
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text-secondary);
}

.gate__input {
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-raised);
  color: var(--text-primary);
  font-family: var(--font-sans);
  font-size: var(--text-base);
  outline: none;
  transition: border-color var(--duration-normal) var(--ease-default);
}

.gate__input:focus {
  border-color: var(--color-brand-primary);
}

.gate__error {
  font-size: var(--text-sm);
  color: #e53e3e;
  margin: 0;
}

.gate__btn {
  padding: var(--space-3) var(--space-6);
  background: var(--color-brand-primary);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  cursor: pointer;
  transition: opacity var(--duration-normal) var(--ease-default);
}

.gate__btn:hover { opacity: 0.85; }

/* ─── Dashboard header ──────────────────────────────────────────────────── */
.dash-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-10);
}

.dash-title {
  font-family: var(--font-display);
  font-size: var(--text-3xl);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--space-1);
}

.dash-subtitle {
  font-size: var(--text-sm);
  color: var(--text-tertiary);
}

.loading {
  color: var(--text-secondary);
  font-size: var(--text-base);
}

/* ─── Filter ────────────────────────────────────────────────────────────── */
.filter-wrap {
  position: relative;
  flex-shrink: 0;
}

.filter-pill {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  background: var(--surface-raised);
  border: 1px solid var(--border-default);
  border-radius: 999px;
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-default),
    color var(--duration-fast) var(--ease-default);
  white-space: nowrap;
}

.filter-pill:hover {
  border-color: var(--color-brand-primary);
  color: var(--text-primary);
}

.filter-pill__chevron {
  transition: transform var(--duration-fast) var(--ease-default);
  color: var(--text-tertiary);
}

.filter-pill__chevron--open {
  transform: rotate(180deg);
}

/* Backdrop */
.filter-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
}

/* Panel */
.filter-panel {
  position: absolute;
  top: calc(100% + var(--space-2));
  right: 0;
  z-index: 50;
  min-width: 220px;
  background: var(--surface-elevated);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.filter-panel__heading {
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  color: var(--text-tertiary);
  margin-bottom: var(--space-3);
}

/* Preset radio list */
.filter-presets {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-bottom: var(--space-4);
}

.filter-radio {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-2);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-default);
}

.filter-radio:hover {
  background: var(--surface-raised);
}

/* Hide native radio */
.filter-radio input[type="radio"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

/* Custom radio dot */
.filter-radio__dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--border-default);
  background: var(--surface-bg);
  flex-shrink: 0;
  position: relative;
  transition:
    border-color var(--duration-fast) var(--ease-default),
    background var(--duration-fast) var(--ease-default);
}

.filter-radio__dot::after {
  content: '';
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: #fff;
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-default);
}

.filter-radio input[type="radio"]:checked ~ .filter-radio__dot {
  border-color: var(--color-brand-primary);
  background: var(--color-brand-primary);
}

.filter-radio input[type="radio"]:checked ~ .filter-radio__dot::after {
  opacity: 1;
}

.filter-radio__label {
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  transition: color var(--duration-fast) var(--ease-default);
}

.filter-radio input[type="radio"]:checked ~ .filter-radio__dot ~ .filter-radio__label {
  color: var(--text-primary);
  font-weight: var(--weight-medium);
}

/* Custom date inputs */
.filter-custom {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.filter-custom__field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.filter-custom__label {
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--text-tertiary);
}

.filter-custom__input {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-raised);
  color: var(--text-primary);
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  outline: none;
  transition: border-color var(--duration-fast) var(--ease-default);
}

.filter-custom__input:focus {
  border-color: var(--color-brand-accent);
}

/* Apply button */
.filter-apply {
  width: 100%;
  padding: var(--space-2) var(--space-4);
  background: var(--color-brand-primary);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  cursor: pointer;
  transition: opacity var(--duration-fast) var(--ease-default);
}

.filter-apply:hover { opacity: 0.85; }

/* ─── Stat cards ────────────────────────────────────────────────────────── */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-10);
}

.stat-card {
  background: var(--surface-raised);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
}

.stat-card__label {
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  margin-bottom: var(--space-3);
}

.stat-card__value {
  font-family: var(--font-display);
  font-size: var(--text-3xl);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  line-height: 1;
}

/* ─── Sections ──────────────────────────────────────────────────────────── */
.section {
  margin-bottom: var(--space-10);
}

.section__title {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: var(--weight-semibold);
  color: var(--text-primary);
  margin-bottom: var(--space-5);
}

.table-empty {
  color: var(--text-tertiary);
  font-size: var(--text-sm);
}

/* ─── Tables ────────────────────────────────────────────────────────────── */
.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table__th {
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
  text-align: left;
}

.data-table__th--right,
.data-table__td--right {
  text-align: right;
  width: 1%;
  white-space: nowrap;
}

.data-table__row:hover {
  background: var(--surface-raised);
}

.data-table__td {
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-primary);
  padding: var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
}

.data-table__td--muted {
  color: var(--text-tertiary);
}

.clicks-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  padding: var(--space-1) var(--space-2);
  background: var(--surface-elevated);
  border-radius: var(--radius-sm);
  font-weight: var(--weight-semibold);
  color: var(--text-primary);
}
</style>
